import { Meta } from '@insta-club/models';
import { useCallback, useMemo } from 'react';
import useSWRInfinite, { SWRInfiniteResponse } from 'swr/infinite';

interface PaginatedResponse<T> {
    data: T[];
    meta: Meta;
}

interface UseInfiniteScrollOptions {
    pageSize?: number;
    initialPageIndex?: number;
}

interface UseInfiniteScrollParams {
    page: number;
    per_page: number;
}

interface UseInfiniteScrollReturn<T> extends Omit<SWRInfiniteResponse<PaginatedResponse<T>>, 'data'> {
    data: T[];
    meta?: Meta;
    isLoadingInitial: boolean;
    isLoadingMore: boolean;
    isEmpty: boolean;
    isReachingEnd: boolean;
    isRefreshing: boolean;
    totalItems: number;
    totalPages: number;
    currentPage: number;
    loadMore: () => void;
    refresh: () => void;
    updateItem: (predicate: (item: T) => boolean, updater: (item: T) => T) => void;
}

export default function useInfiniteScroll<T>(
    baseUrl: string,
    fetcher: ([url, params]: [string, UseInfiniteScrollParams]) => Promise<PaginatedResponse<T>>,
    options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollReturn<T> {
    const { pageSize = 10, initialPageIndex = 1 } = options;

    const getKey = useCallback(
        (pageIndex: number, previousPageData: PaginatedResponse<T> | null) => {
            if (previousPageData && previousPageData.meta.current_page >= previousPageData.meta.last_page) {
                return null;
            }
            return [baseUrl, { page: pageIndex + initialPageIndex, per_page: pageSize }];
        },
        [baseUrl, pageSize, initialPageIndex]
    );

    const {
        data: pages,
        error,
        size,
        setSize,
        isValidating,
        isLoading,
        mutate
    } = useSWRInfinite<PaginatedResponse<T>>(getKey, fetcher, { revalidateFirstPage: false });

    const data = useMemo(() => {
        if (!pages) return [];
        return pages.flatMap((page) => page.data);
    }, [pages]);

    const meta = useMemo(() => {
        if (!pages || pages.length === 0) return undefined;
        return pages[pages.length - 1].meta;
    }, [pages]);

    const isLoadingInitial = !pages && !error;

    const isLoadingMore = Boolean(isLoadingInitial || (size > 0 && pages && typeof pages[size - 1] === 'undefined'));

    const isEmpty = data.length === 0;

    const isReachingEnd = Boolean(isEmpty || (meta && meta.current_page >= meta.last_page));

    const isRefreshing = Boolean(isValidating && pages && pages.length === size);

    const totalItems = meta?.total ?? 0;

    const totalPages = meta?.last_page ?? 0;

    const currentPage = meta?.current_page ?? 0;

    const loadMore = useCallback(() => {
        if (!isLoadingMore && !isReachingEnd) {
            setSize(size + 1);
        }
    }, [isLoadingMore, isReachingEnd, setSize, size]);

    const refresh = useCallback(() => {
        mutate();
    }, [mutate]);

    const updateItem = useCallback(
        (predicate: (item: T) => boolean, updater: (item: T) => T) => {
            if (!pages) return;

            const updatedPages = pages.map((page) => ({
                ...page,
                data: page.data.map((item) => (predicate(item) ? updater(item) : item))
            }));

            mutate(updatedPages, false);
        },
        [pages, mutate]
    );

    return {
        data,
        meta,
        error,
        size,
        setSize,
        isValidating,
        isLoading,
        mutate,
        isLoadingInitial,
        isLoadingMore,
        isEmpty,
        isReachingEnd,
        isRefreshing,
        totalItems,
        totalPages,
        currentPage,
        loadMore,
        refresh,
        updateItem
    };
}

